import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type TextareaHTMLAttributes,
} from 'react'
import { Link2, Plus } from 'lucide-react'
import {
  createBibleEntry,
  referenceTargets,
  referenceMentions,
  type ReferencePick,
} from '../connectedBible'
import type { StoryProject } from '../types'

type Props = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
> & {
  value: string
  project: StoryProject
  sourceId: string
  grow?: boolean
  onText: (value: string, pick?: ReferencePick) => void
  onOpenReference: (id: string) => void
}
export function ReferenceTextarea({
  value,
  project,
  sourceId,
  onText,
  onOpenReference,
  grow,
  ...props
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const menuId = useId()
  const [query, setQuery] = useState<{
    start: number
    end: number
    text: string
  } | null>(null)
  const [active, setActive] = useState(0)
  const targets = referenceTargets(project).filter((t) => t.id !== sourceId)
  const matches = targets
    .filter((t) =>
      t.title.toLowerCase().includes(query?.text.toLowerCase() || ''),
    )
    .slice(0, 7)
  const canCreate =
    !!query?.text.trim() &&
    !targets.some(
      (t) => t.title.toLowerCase() === query.text.trim().toLowerCase(),
    )
  const count = matches.length + (canCreate ? 1 : 0)
  useLayoutEffect(() => {
    if (!grow || !ref.current) return
    const element = ref.current
    const resize = () => {
      element.style.height = '0px'
      element.style.height = Math.max(element.scrollHeight, 36) + 'px'
    }
    resize()
    let width = element.clientWidth
    let frame = 0
    const observer = new ResizeObserver(() => {
      if (width !== element.clientWidth) {
        width = element.clientWidth
        // Changing the observed parent's height inside its resize callback can
        // trigger a WebKit ResizeObserver loop when a long draft changes width.
        cancelAnimationFrame(frame)
        frame = requestAnimationFrame(resize)
      }
    })
    if (element.parentElement) observer.observe(element.parentElement)
    return () => { observer.disconnect(); cancelAnimationFrame(frame) }
  }, [value, grow])
  const choose = (index: number) => {
    if (!query) return
    const target = matches[index]
    const entry =
      !target && canCreate ? createBibleEntry(query.text.trim()) : undefined
    if (!target && !entry) return
    const name = target?.title || entry!.title
    const updated =
      value.slice(0, query.start) + '@' + name + ' ' + value.slice(query.end)
    onText(updated, {
      targetId: target?.id || 'entry:' + entry!.id,
      ...(entry ? { entry } : {}),
    })
    setQuery(null)
    requestAnimationFrame(() => {
      ref.current?.focus()
      ref.current?.setSelectionRange(
        query.start + name.length + 2,
        query.start + name.length + 2,
      )
    })
  }
  const mentionedIds = new Set(
    referenceMentions(project, sourceId, value).flatMap((m) => m.targetIds),
  )
  const mentioned = targets.filter((t) => mentionedIds.has(t.id))
  return (
    <div
      className={
        'reference-input' + (grow ? ' reference-input--growing' : '')
      }
    >
      <textarea
        {...props}
        ref={ref}
        value={value}
        aria-controls={query ? menuId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          query && count ? menuId + '-' + active : undefined
        }
        onBlur={(event) => {
          props.onBlur?.(event)
          if (
            !event.currentTarget.parentElement?.contains(event.relatedTarget)
          )
            setQuery(null)
        }}
        onChange={(event) => {
          const text = event.target.value,
            end = event.target.selectionStart
          onText(text)
          const match = text
            .slice(0, end)
            .match(/(?:^|[^\p{L}\p{N}_@])@([^\n@]{0,80})$/u)
          if (match && (query || text[end - 1] === '@')) {
            setQuery({
              start: end - match[1].length - 1,
              end,
              text: match[1],
            })
            setActive(0)
          } else setQuery(null)
        }}
        onKeyDown={(event) => {
          if (
            query &&
            ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)
          ) {
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              setQuery(null)
              return
            }
            if (count) {
              event.preventDefault()
              if (event.key === 'Enter') choose(active)
              else
                setActive(
                  (i) =>
                    (i + (event.key === 'ArrowDown' ? 1 : count - 1)) % count,
                )
              return
            }
          }
          props.onKeyDown?.(event)
        }}
      />
      {query && (
        <div
          className="reference-suggestions"
          id={menuId}
          role="listbox"
          aria-label="Link to a story entry"
        >
          <small>LINK TO YOUR WORLD</small>
          {matches.map((target, index) => (
            <button
              id={menuId + '-' + index}
              role="option"
              aria-selected={index === active}
              type="button"
              key={target.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(index)}
            >
              <Link2 size={14} />
              <span>
                {target.title}
                <small>{target.section}</small>
              </span>
            </button>
          ))}
          {canCreate && (
            <button
              id={menuId + '-' + matches.length}
              role="option"
              aria-selected={active === matches.length}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(matches.length)}
            >
              <Plus size={14} /> Create “{query.text.trim()}” in Locations
            </button>
          )}
          {!count && <p>Type a name to find or create an entry.</p>}
        </div>
      )}
      {!!mentioned.length && (
        <div className="reference-chips">
          {mentioned.map((t) => (
            <button
              type="button"
              key={t.id}
              onClick={() => onOpenReference(t.id)}
            >
              <Link2 size={11} />
              {t.title}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
